<template>
  <v-container>
    <ConfirmationDialog2 ref="confirmationDialog" />
    <PageHeader subtitle="Manage access permission for users across the system." />
    <Subheader v-model="search" title="Tenants" search="tenants">
      <v-btn dark color="success" @click="showAddRole = true">
        <v-icon>add</v-icon>
        {{ $t("Button.Add_a_new_tenant") }}
      </v-btn>
    </Subheader>

    <v-card>
      <v-card-text>
        <v-item-group mandatory v-model="filters.status" active-class="white--text">
          <v-subheader style="display: inline; cursor: default;">Show:</v-subheader>
          <v-item value="active">
            <v-chip small slot-scope="{ active, toggle }" :color="active ? 'primary' : 'default'" :selected="active" class="chipFilter clickable" @click="toggle">
              Active tenants ⋅
              {{ activeTenants.length }}
            </v-chip>
          </v-item>
          <v-item value="archived">
            <v-chip small slot-scope="{ active, toggle }" :color="active ? 'primary' : 'default'" :selected="active" class="chipFilter clickable" @click="toggle">
              Archived tenants ⋅
              {{ archivedTenants.length }}
            </v-chip>
          </v-item>
        </v-item-group>
        <v-data-table :headers="headers" :items="filteredTenants" :pagination.sync="pagination" :search="search" :expand="false" item-key="RoleName">
          <template v-slot:items="props">
            <tr @click="props.expanded = !props.expanded">
              <td>
                <div>
                  {{ props.item.RoleName }}
                </div>
                <div class="caption" v-if="props.item.IsArchived">Archived on {{ formatDate(props.item.ArchivedDateTime, prettyDateTimeFormat)}}</div>
              </td>
              <td class="text-xs-right">
                <template v-if="filters.status != 'archived'">
                  <SecondaryBtn small icon="settings" :to="{ name: 'accessManageEntrance', params: { name: props.item.RoleName,id: props.item.Id }}">
                    {{ $t("Button.Manage") }}
                  </SecondaryBtn>
                  <SecondaryBtn :disabled="props.item.Id == currentTenant.id" @click="confirmArchiveTenant(props.item)" small icon="archive" color="error">
                    {{ $t("Button.Archive") }}
                  </SecondaryBtn>
                </template>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-dialog v-model="showAddRole" persistent max-width="800px">
      <v-card>
        <v-toolbar dark color="primary">
          <v-toolbar-title>{{
            $t("AdministrationAccessManage.addTitleMsg")
          }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click.native="showAddRole = false; loading.addNewRole = false" dark>
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-form v-model="valid" ref="form" lazy-validation class="fullScreenWidth">
          <v-card-text>
            <v-layout row wrap>
              <v-flex xs12 pa-3>
                <v-text-field label="Tenant Name" v-model="newRoleName" :rules="nameRules"></v-text-field>
              </v-flex>
            </v-layout>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="success" :loading="loading.addNewRole" :disabled="!!!newRoleName" @click="addNewRole()">
              <v-icon class="mr-1">done</v-icon>
              {{ $t("Button.Save") }}
            </v-btn>
            <v-btn color="secondary" outline @click="clearForm()">{{
              $t("Button.Close")
            }}</v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
  import { prettyDateTimeFormat, formatDate } from '@/components/mixins/MomentUtil'

  import { createNamespacedHelpers } from 'vuex'
  const authenticate = createNamespacedHelpers('authenticate')
  const app = createNamespacedHelpers('app')
  const admin = createNamespacedHelpers('admin')
  const tenants = createNamespacedHelpers('tenants')

  export default {
    data() {
      return {
        prettyDateTimeFormat,
        search: '',
        pagination: {
          rowsPerPage: -1,
        },
        headers: [{
          align: 'left',
          text: 'Tenant',
          value: 'RoleName',
        },
        {
          sortable: false,
          text: '',
          value: '',
        },
        ],
        dialog: false,
        showAddRole: false,
        valid: true,
        newRoleName: '',
        deleteRole: {},
        loading: {
          addNewRole: false,
        },
        filters: {
          status: 'active',
        },
        nameRules: [
          v => !!v || 'Tenant name is required',
          v =>
          this.roleList.find(x => x.RoleName == v) == null ||
          'This tenant name is already used in the system.',
        ],
      }
    },
    mounted() {
      this.getRoleList()
    },
    computed: {
      ...admin.mapGetters(['roleList']),
      ...authenticate.mapGetters(['currentTenant']),
      tenants() {
        return this.roleList || []
      },
      archivedTenants() {
        return this.tenants.filter(tenant => tenant.IsArchived)
      },
      activeTenants() {
        return this.tenants.filter(tenant => !tenant.IsArchived)
      },
      filteredTenants() {
        if (this.filters.status == 'active') return this.activeTenants
        else return this.archivedTenants
      },
    },
    methods: {
      ...admin.mapActions(['getRoleList', 'postAddRole', 'postDeleteRole']),
      ...app.mapActions(['setSnackbar']),
      ...tenants.mapActions(['archiveTenant']),
      formatDate,
      async confirmArchiveTenant(tenant) {
        const params = {
          title: 'Archive tenant',
          text: `<div>Are you sure you want to archive tenant <strong>${tenant.RoleName}</strong>?</div><br/>
           <div>The tenant's data will be preserved, but will not be accessible. Tenant restoration is a manual process.</div>`,
          confirmText: 'Archive',
          cancelText: 'Cancel',
          color: 'error',
          icon: 'archive',
        }

        let confirmed = await this.$refs.confirmationDialog.confirm(params)
        if (!confirmed) return

        try {
          await this.archiveTenant({ tenantId: tenant.Id })
          this.setSnackbar({
            color: 'success',
            icon: 'done',
            text: 'Tenant archived',
          })
          await this.getRoleList()
        } catch (error) {
          console.error(error)
          this.setSnackbar({
            color: 'error',
            icon: 'error',
            text: error.Message,
          })
        }
      },
      addNewRole() {
        if (this.newRoleName && this.newRoleName.length) {
          this.loading.addNewRole = true
          let name = this.newRoleName.trim()
          if (this.$refs.form.validate()) {
            let options = {
              data: {
                RoleName: name,
              },
              successCb: () => {
                this.setSnackbar({
                  color: 'success',
                  icon: 'done',
                  text: this.$t('AdministrationAccessManage.addSuccessMsg', {
                    msg: this.newRoleName,
                  }),
                })
                this.loading.addNewRole = false
                this.clearForm()
              },
              failedCb: err => {
                this.setSnackbar({
                  color: 'error',
                  icon: 'error',
                  text: err,
                })
                this.loading.addNewRole = false
              },
            }
            this.postAddRole(options)
          }
        }
      },
      agreeDeleteRole() {
        let options = {
          data: {
            Id: this.deleteRole.id,
            RoleName: this.deleteRole.roleName,
          },
          successCb: () => {
            this.setSnackbar({
              color: 'success',
              icon: 'done',
              text: this.$t('AdministrationAccessManage.deleteSuccessMsg', {
                msg: this.deleteRole.roleName,
              }),
            })
            this.dialog = false
          },
          failedCb: err => {
            this.setSnackbar({
              color: 'error',
              icon: 'error',
              text: err,
            })
          },
        }
        this.postDeleteRole(options)
      },
      clearForm() {
        this.$refs.form.reset()
        this.showAddRole = false
        this.loading.addNewRole = false
      },
    },
  }
</script>

<style scoped>
  .toolbar__title.display-2 {
    padding-bottom: 10px;
    margin-left: 10px !important;
  }

  .fullScreenWidth {
    width: 100%;
  }

  .titleAdjust {
    padding-top: 50px;
  }

  .iconAdjust {
    padding-bottom: 10px;
  }
</style>